import _extends from "@babel/runtime/helpers/extends";
import React, { useEffect } from "react";
import styled, { useTheme } from "styled-components";
import Button from "~/components-shared/Button";
import mixpanel from "mixpanel-browser";
import { useTranslation } from "react-i18next";
import CompanyForm from "~/forms/CompanyForm";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import Card from "~/components-shared/Card";
import companySchema from "~/forms/CompanyForm/companySchema";
import Icon from "~/components-shared/Icon";
import { useDispatch, useSelector } from "react-redux";
import onboardingActionCreators from "~/store/onboarding/actions";
import authSelectors from "~/store/auth/selectors";
import { helpers, onboardingOrder } from "~/lib/helpers";
import { StatusStateEnum, MemberStatusEnum } from "~/types/enums";
import { Alert, Box } from "~/components-shared";
import onboardingSelectors from "~/store/onboarding/selectors";
import companyActionCreators from "~/store/company/actions";
import companySelectors from "~/store/company/selectors";
const Form = styled.form`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
`;
const Content = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
`;
const StyledIcon = styled(Icon)`
  box-shadow: inset 0 0 0 6px ${_ref => {
  let {
    theme
  } = _ref;
  return theme.colors.success.main;
}};
  border-radius: 50%;
`;
const CompanyInformationCard = _ref2 => {
  let {
    open,
    ...rest
  } = _ref2;
  const {
    colors
  } = useTheme();
  const {
    t
  } = useTranslation();
  const user = useSelector(authSelectors.getUser);
  const companyStatus = useSelector(companySelectors.getStatus);
  const company = useSelector(companySelectors.getCompany);
  const nextStatus = useSelector(onboardingSelectors.getStatus);
  const backStatus = useSelector(onboardingSelectors.getBackStatus);
  const nextError = useSelector(onboardingSelectors.getError);
  const backError = useSelector(onboardingSelectors.getBackError);
  const availableStatuses = helpers.memberOnboardingStatuses(user.authorities);
  const statusIndex = availableStatuses.findIndex(item => item === user.status);
  const error = nextError || backError;
  const success = user?.status && onboardingOrder[user.status] > onboardingOrder[MemberStatusEnum.ONBOARDING_COMPANY_INFORMATION];
  const dispatch = useDispatch();
  const defaultValues = {
    name: company?.name || "",
    description: company?.description || "",
    url: company?.url || "",
    employeeCount: company?.employeeCount || 0,
    emailDomains: company?.emailDomains || ""
  };
  const {
    handleSubmit,
    control,
    watch,
    setValue,
    setError,
    reset
  } = useForm({
    resolver: yupResolver(companySchema),
    defaultValues
  });
  useEffect(() => {
    if (companyStatus === StatusStateEnum.IDLE) {
      dispatch(companyActionCreators.fetchStarted());
    }
  }, []);
  useEffect(() => {
    if (company) {
      reset(defaultValues);
    }
  }, [company]);
  const onBack = () => {
    dispatch(onboardingActionCreators.backStarted());
    mixpanel.track(`Onboarding Company Info – Back`);
  };
  const onSubmit = handleSubmit(data => {
    dispatch(onboardingActionCreators.companyInfoSubmitStarted(data));
    mixpanel.track(`Onboarding Company Info – Submit`, {
      ...helpers.objectDiff(defaultValues, data)
    });
  });
  return /*#__PURE__*/React.createElement(Card, _extends({
    s: {
      px: 10,
      py: 6,
      mb: 10
    },
    collapsed: !open,
    elemAfter: success ? /*#__PURE__*/React.createElement(StyledIcon, {
      src: "checkboxFill",
      scale: 8,
      color: colors.success.main
    }) : undefined,
    shadow: true
  }, rest), /*#__PURE__*/React.createElement(Form, {
    onSubmit: onSubmit
  }, error && /*#__PURE__*/React.createElement(Alert, {
    severity: "danger",
    s: {
      mb: 3
    },
    fullWidth: true
  }, error), /*#__PURE__*/React.createElement(Content, null, /*#__PURE__*/React.createElement(CompanyForm, {
    control: control,
    watch: watch,
    setError: setError,
    setValue: setValue,
    company: company
  })), /*#__PURE__*/React.createElement(Box, {
    direction: "row",
    gap: 5,
    justify: "end",
    s: {
      mt: 10,
      mb: 2
    },
    fullWidth: true
  }, statusIndex !== 0 && /*#__PURE__*/React.createElement(Button, {
    elemBefore: /*#__PURE__*/React.createElement(Icon, {
      src: "arrow",
      scale: 5,
      rotate: 180,
      s: {
        mr: 2
      }
    }),
    color: "translucent",
    type: "submit",
    onClick: onBack,
    loading: backStatus === StatusStateEnum.LOADING,
    s: {
      pl: 4,
      pr: 6
    },
    rounded: true
  }, t("common.actions.back")), /*#__PURE__*/React.createElement(Button, {
    color: "primary",
    maxWidth: "230px",
    type: "submit",
    onClick: onSubmit,
    loading: nextStatus === StatusStateEnum.LOADING,
    fullWidth: true,
    rounded: true
  }, t("common.actions.save")))));
};
export default CompanyInformationCard;